import "./ClaimNumberInput.scss";

import cx from "classnames";
import { KaboodleToaster } from "components/common";
import { DatePicker, TextInput } from "components/inputs";
import { LoadingIcon } from "components/Loading/LoadingIcon";
import SectionBreak from "components/SectionBreak";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  useCreateDateOfInjuryForFileMutation,
  useDeleteDateOfInjuryForFileMutation,
  usePatchDateOfInjuryByIdMutation,
  useCreateServiceDateOfInjuryForServiceMutation,
} from "services/doi-api";
import {
  useCreateClaimNumberMutation,
  useDeleteClaimNumberMutation,
  useGetFileByIdQuery,
  usePatchClaimNumberByIdMutation,
} from "services/file-api.ts";
import {
  DATE_TO_STRING,
  STRING_TO_DATE,
  dateComparator,
} from "utils/date-utils";
import type { FileType } from "types/DataTypes";

export type DateOfInjuryType = {
  id?: number;
  date: string;
  organization?: string;
  file: number;
};

export type ClaimNumberType = {
  id?: number;
  organization?: string;
  file: number;
  date_of_injury_id: number;
  number: string;
};

export type MultiClaimDateInputProps = {
  dateOfInjury: DateOfInjuryType;
  initialClaimNumbers: ClaimNumberType[];
  canDeleteDoi: boolean;
  canAddDoi: boolean;
  onAddDoi: (date: Date | null) => void;
  isReadOnly?: boolean;
};

type ActionButtonsProps = {
  onAdd: () => void;
  onDelete: () => void;
  canAdd?: boolean;
  canDelete?: boolean;
  isLoading?: boolean;
  isReadOnly?: boolean;
};

/**
 * Action button component for adding/deleting items
 */
const ActionButtons = ({
  onAdd,
  onDelete,
  canAdd = true,
  canDelete = false,
  isLoading = false,
  isReadOnly = false,
}: ActionButtonsProps) => (
  <figure className="buttons-container-add-delete">
    <div className="flex jc-end">
      {!isReadOnly && canAdd && (
        <button
          type="button"
          onClick={!isLoading ? onAdd : undefined}
          className={cx("btn add", { "btn--loading": isLoading })}
          aria-label="Add Field"
          disabled={isLoading}
        >
          {isLoading ? (
            <LoadingIcon showText={false} size="small" />
          ) : (
            <span className="material-icons">add</span>
          )}
        </button>
      )}
      {!isReadOnly && canDelete && (
        <button
          type="button"
          onClick={!isLoading ? onDelete : undefined}
          className="btn delete"
          aria-label="Delete Field"
          disabled={isLoading}
        >
          <span className="material-icons">delete</span>
        </button>
      )}
    </div>
  </figure>
);

/**
 * Component for managing a date of injury and its associated claim numbers
 */
const MultiClaimDateInput = ({
  dateOfInjury,
  initialClaimNumbers,
  canDeleteDoi,
  onAddDoi,
  canAddDoi,
  isReadOnly,
  totalDates,
}: MultiClaimDateInputProps & { totalDates: number; fileData: FileType }) => {
  const [claimNumbers, setClaimNumbers] = useState([]);
  const [isAddingClaimNumber, setIsAddingClaimNumber] = useState(false);
  const { fileId } = useParams<{ fileId: string }>();

  // API hooks
  const [addDatesOfInjuryForFile, { isLoading: isAddingDoi }] =
    useCreateDateOfInjuryForFileMutation();
  const [deleteDatesOfInjury, { isLoading: isDeletingDoi }] =
    useDeleteDateOfInjuryForFileMutation();
  const [patchDateOfInjury, { isLoading: isUpdatingDoi }] =
    usePatchDateOfInjuryByIdMutation();
  const [createClaimNumberForFile] = useCreateClaimNumberMutation();
  const [deleteClaimNumber] = useDeleteClaimNumberMutation();
  const [patchClaimantNumberById] = usePatchClaimNumberByIdMutation();
  useCreateServiceDateOfInjuryForServiceMutation();
  // helper functions for button visibility
  const canAddClaimNumber = (index: number) => {
    // only allow adding if it's the last one AND it has a value AND we're not currently adding one
    const lastClaimNumber = claimNumbers[claimNumbers.length - 1];
    const isLastClaimNumberFilled = lastClaimNumber?.number?.trim();
    return (
      index === claimNumbers.length - 1 &&
      !isReadOnly &&
      isLastClaimNumberFilled &&
      !isAddingClaimNumber
    );
  };

  const canDeleteClaimNumber = (
    claimNumber: ClaimNumberType,
    totalCount: number,
  ) => {
    // Allow delete if there's more than one claim number and it's not the only empty one
    return (
      totalCount > 1 &&
      !isReadOnly &&
      (!!claimNumber?.number?.trim() || totalCount > 2)
    );
  };

  const shouldShowButtons = (index: number, claimNumber: ClaimNumberType) => {
    const canAdd = canAddClaimNumber(index);
    const canDelete = canDeleteClaimNumber(claimNumber, claimNumbers.length);
    return canAdd || canDelete;
  };

  // Initialize claim numbers based on date of injury
  useEffect(() => {
    const claimNumbersByDoi = initialClaimNumbers.filter(
      (cn) => cn.date_of_injury_id === dateOfInjury?.id,
    );

    setClaimNumbers(
      claimNumbersByDoi?.length ? claimNumbersByDoi : [{ number: "" }],
    );
  }, [initialClaimNumbers, dateOfInjury?.id]);

  // Handle date of injury CRUD operations
  const handleDateOfInjury = useCallback(
    async (action: "add" | "edit" | "delete", { date, id }) => {
      try {
        switch (action) {
          case "add":
            await addDatesOfInjuryForFile({
              data: {
                file: fileId,
                date,
              },
            }).unwrap();
            KaboodleToaster.success("Date of injury added successfully");
            break;
          case "edit":
            await patchDateOfInjury({
              id,
              data: { date },
            }).unwrap();
            KaboodleToaster.success("Date of injury updated successfully");
            break;
          case "delete":
            await deleteDatesOfInjury({
              id,
            }).unwrap();
            KaboodleToaster.success("Date of injury deleted successfully");
            break;
          default:
            break;
        }
      } catch (error) {
        KaboodleToaster.error(error);
      }
    },
    [fileId, patchDateOfInjury, addDatesOfInjuryForFile, deleteDatesOfInjury],
  );

  // Handle claim number CRUD operations with loading state
  const handleClaimNumbers = useCallback(
    async (action: "add" | "edit" | "delete", { number, id }) => {
      const baseData = {
        file: fileId,
        number,
        date_of_injury_id: dateOfInjury?.id?.toString(),
      };

      try {
        if (action === "add" && number.trim()) {
          // Only set loading if we're actually making an API call
          setIsAddingClaimNumber(true);
          await createClaimNumberForFile({
            data: baseData,
          }).unwrap();
          KaboodleToaster.success("Claim number added successfully");
        } else if (action === "edit") {
          await patchClaimantNumberById({
            id: Number(id),
            data: baseData,
          }).unwrap();
          KaboodleToaster.success("Claim number updated successfully");
        } else if (action === "delete") {
          await deleteClaimNumber({
            id: String(id),
          }).unwrap();
          KaboodleToaster.success("Claim number deleted successfully");
        }
      } catch (error) {
        KaboodleToaster.error(error);
      } finally {
        if (action === "add" && number.trim()) {
          setIsAddingClaimNumber(false);
        }
      }
    },
    [
      fileId,
      dateOfInjury,
      deleteClaimNumber,
      patchClaimantNumberById,
      createClaimNumberForFile,
    ],
  );

  // Debounce claim number changes to prevent excessive API calls
  const debouncedHandleClaimNumbers = useCallback(
    debounce(
      (action: "add" | "edit" | "delete", value) =>
        handleClaimNumbers(action, value),
      500,
    ),
    [handleClaimNumbers],
  );

  // Add a new claim number to the state with optimistic update
  const addClaimNumber = () => {
    if (!isAddingClaimNumber) {
      setIsAddingClaimNumber(true);
      setClaimNumbers((prev) => [...prev, { number: "" }]);
      // Reset the loading state after the optimistic update
      setIsAddingClaimNumber(false);
    }
  };

  // Update a claim number value
  const updateClaimNumber = (index: number, value: string) => {
    setClaimNumbers((prev) => {
      const newValues = [...prev];
      newValues[index] = {
        ...prev[index],
        number: value,
      };
      return newValues;
    });
  };

  return (
    <div className="multi-date-picker-container">
      <SectionBreak />
      <div className="multi-row-adjust">
        <label>Date Of Injury</label>
        <div
          className={cx("input-container", {
            "input--with-buttons": canAddDoi || canDeleteDoi,
            "input--disabled": isReadOnly,
          })}
        >
          <DatePicker
            name="date_of_injury"
            initialDate={STRING_TO_DATE(dateOfInjury?.date)}
            className={cx("datepicker", {
              "w-two-btns": canAddDoi && canDeleteDoi,
              "datepicker--with-buttons":
                (canAddDoi || (!!dateOfInjury?.id && canDeleteDoi)) &&
                !isReadOnly,
            })}
            onDateChange={(date) => {
              handleDateOfInjury(dateOfInjury?.id ? "edit" : "add", {
                date: DATE_TO_STRING(date),
                id: dateOfInjury?.id?.toString(),
              });
            }}
            readOnly={
              isReadOnly || isAddingDoi || isDeletingDoi || isUpdatingDoi
            }
            isClearable={totalDates > 1}
          />
          <ActionButtons
            onAdd={() => onAddDoi(null)}
            onDelete={() =>
              handleDateOfInjury("delete", {
                id: dateOfInjury.id?.toString(),
                date: dateOfInjury.date,
              })
            }
            canAdd={canAddDoi}
            canDelete={!!dateOfInjury?.id && canDeleteDoi}
            isReadOnly={isReadOnly}
            isLoading={isAddingDoi || isDeletingDoi || isUpdatingDoi}
          />
        </div>

        {claimNumbers?.map((claimNumber, index) => (
          <div className="multi-row-adjust" key={index}>
            <label>{`Claim Number ${index + 1}`}</label>
            <div
              className={cx("input-container", {
                "input--with-buttons": shouldShowButtons(index, claimNumber),
                "input--disabled": !dateOfInjury?.date || isReadOnly,
              })}
            >
              <TextInput
                name="claim_number"
                value={claimNumber?.number || ""}
                readOnly={
                  !dateOfInjury?.date || isReadOnly || isAddingClaimNumber
                }
                className="input"
                onChange={(e) => {
                  const newValue = e.target.value;
                  updateClaimNumber(index, newValue);
                  debouncedHandleClaimNumbers(
                    claimNumber?.id ? "edit" : "add",
                    {
                      number: newValue,
                      id: claimNumber?.id?.toString(),
                    },
                  );
                }}
              />
              <ActionButtons
                onAdd={addClaimNumber}
                onDelete={() => {
                  debouncedHandleClaimNumbers("delete", {
                    id: claimNumber.id?.toString(),
                    number: claimNumber.number,
                  });
                }}
                canAdd={canAddClaimNumber(index)}
                canDelete={canDeleteClaimNumber(
                  claimNumber,
                  claimNumbers.length,
                )}
                isReadOnly={isReadOnly}
                isLoading={isAddingClaimNumber}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ClaimNumberInput = ({
  initialClaimNumbers,
  isReadOnly = false,
}: {
  initialClaimNumbers: ClaimNumberType[];
  isReadOnly?: boolean;
}) => {
  const { fileId } = useParams<{ fileId: string }>();
  const [dates, setDates] = useState([]);

  const { data: fileData } = useGetFileByIdQuery(
    { id: fileId },
    { skip: !fileId },
  );

  useEffect(() => {
    if (fileData?.dates_of_injury) {
      // sort to maintain consistency when the DOIs change
      const sorted = [...fileData.dates_of_injury].sort(dateComparator);
      setDates(sorted);
    }
  }, [fileData]);

  const handleAddDateOfInjury = (date: Date | null) => {
    const newDates = [...dates, { date: DATE_TO_STRING(date) }];
    // sort to maintain consistency when the DOIs change
    newDates.sort(dateComparator);
    setDates(newDates);
  };

  return (
    <>
      {dates && dates.length > 0 ? (
        dates.map((dateOfInjury, index) => (
          <MultiClaimDateInput
            key={`doi-${dateOfInjury.id || index}`}
            canDeleteDoi={dates.length > 1 && index > 0}
            // prevent adding a new date of injury if the previous one has no date, or if it's the last one
            canAddDoi={index === dates.length - 1 && !!dateOfInjury.date}
            onAddDoi={handleAddDateOfInjury}
            dateOfInjury={dateOfInjury}
            initialClaimNumbers={initialClaimNumbers}
            isReadOnly={isReadOnly}
            totalDates={dates.length}
            fileData={fileData}
          />
        ))
      ) : (
        <MultiClaimDateInput
          key="doi-empty"
          canDeleteDoi={false}
          canAddDoi={true}
          onAddDoi={handleAddDateOfInjury}
          dateOfInjury={null}
          initialClaimNumbers={initialClaimNumbers}
          isReadOnly={isReadOnly}
          totalDates={0}
          fileData={fileData}
        />
      )}
    </>
  );
};

export default ClaimNumberInput;
